import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Button, CalendarProps, IconButton, Label } from '@c2fo/liquidity';
import { TrashCanIcon } from '@c2fo/liquidity/icons';
import DatePicker from '@/enterprise/components/DatePicker';
import { useReporting } from '@/reporting';
import { scheduleTimeZone } from '../utils';

const StartOrEndDateInput = ({
  type,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
}: {
  type: 'start' | 'end';
  startDate: string | undefined;
  endDate: string | undefined;
  setStartDate: (date: string | undefined) => void;
  setEndDate: (date: string | undefined) => void;
}) => {
  const { trackEnterpriseEvent } = useReporting();
  const { t } = useTranslation();

  const setAppropriateValue = (dateFromPicker: Date | undefined) => {
    const dateStr = dateFromPicker ? format(dateFromPicker, 'yyyy-MM-dd') : undefined;

    if (type === 'start') {
      setStartDate(dateStr);
      trackEnterpriseEvent('offer::schedule::form::startDate::selected', {
        date: dateStr,
      });
    } else {
      setEndDate(dateStr);
      trackEnterpriseEvent('offer::schedule::form::endDate::selected', {
        date: dateStr,
      });
    }

    if (!dateFromPicker) {
      setShowDatePicker(false);
    }
  };

  // Always display the date in the form value, not the schedule time zone.
  // This is the actual string value we will send through to the platform scheduler.
  const formDateValue = useMemo(() => {
    if (type === 'start') {
      return startDate;
    }

    return endDate;
  }, [type, startDate, endDate]);

  const [showDatePicker, setShowDatePicker] = useState(!!formDateValue);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const text = {
    start: {
      title: t('scheduling.startDate'),
      buttonText: t('scheduling.setStartDate'),
      label: t('scheduling.startDateLabel'),
    },
    end: {
      title: t('scheduling.endDate'),
      buttonText: t('scheduling.setEndDate'),
      label: t('scheduling.endDateLabel'),
    },
  };

  const disabledOptions: CalendarProps<'single'>['disabled'] = useMemo(() => {
    const nowInScheduleTimeZone = new Date(new Date().toLocaleString('en-US', { timeZone: scheduleTimeZone }));

    if (type === 'start') {
      return {
        // Considering the schedule time zone is important for this `before` option, so a user in Australia for example who is in "the future" can still select today's date in the schedule time zone.
        before: nowInScheduleTimeZone,
        // When considering selected days (startDate or endDate), we intentionally do NOT convert the date to the schedule time zone, as it is assumed the visual calendar is in the schedule time zone.
        ...(!!endDate && { after: new Date(`${endDate}T00:00:00`) }),
      };
    }

    if (type === 'end') {
      // If we have a start date, we need to ensure the end date is after the start date.
      // When that start date is in the past, we use the current date in the schedule time zone.
      if (startDate) {
        const startDateValue = new Date(`${startDate}T00:00:00`);
        return { before: nowInScheduleTimeZone > startDateValue ? nowInScheduleTimeZone : startDateValue };
      }

      // If we don't have a start date, we just use the current date in the schedule time zone.
      return { before: nowInScheduleTimeZone };
    }

    return undefined;
  }, [type, endDate, startDate]);

  useEffect(() => {
    if (!formDateValue) {
      setShowDatePicker(false);
    }
  }, [formDateValue]);

  const onClickSetDate = () => {
    setShowDatePicker(true);
    trackEnterpriseEvent(
      type === 'start' ? 'offer::schedule::form::startDate::opened' : 'offer::schedule::form::endDate::opened'
    );
  };

  return !showDatePicker ? (
    <div className="flex flex-col items-start justify-between gap-x-2 gap-y-1 md:flex-row md:items-center">
      <span className="text-base">{text[type].label}</span>
      <Button size="sm" variant="link" onClick={onClickSetDate}>
        {text[type].buttonText}
      </Button>
    </div>
  ) : (
    <div className="flex flex-col gap-1">
      {/* TODO: htmlFor */}
      <Label>{text[type].title}</Label>
      <div className="flex items-center justify-start">
        <DatePicker
          disabledOptions={disabledOptions}
          open={datePickerOpen}
          onOpenChange={() => {
            setDatePickerOpen(!datePickerOpen);
          }}
          onSelect={(date) => {
            setDatePickerOpen(false);

            if (date) {
              setAppropriateValue(date);
            }
          }}
          disableDefaultSelection
          selected={formDateValue ? formDateValue : null}
          triggerClassName="pr-2 w-64"
          trigger={
            <div
              aria-label={t('core.selectDate')}
              className="flex h-12 w-full items-center justify-between rounded-md border bg-white px-4"
              role="button"
            >
              {formDateValue ? formDateValue : t('core.selectDate')}
            </div>
          }
        />
        <IconButton
          className="bg-transparent"
          icon={TrashCanIcon}
          inverse
          name="Remove selected date"
          onClick={() => {
            setAppropriateValue(undefined);
          }}
          size="sm"
          variant="destructive"
        />
      </div>
    </div>
  );
};

export default StartOrEndDateInput;
